<template>
    <div ref="formWidgetRef" class="widget-file">
        <div class="widget-file-item">
            <label
                v-if="widgetRef.instanceProperties.label"
                class="field-label"
                :class="{ 'pointer-event-none': mode == 'edit' }"
                :for="widgetRef.instanceProperties.id"
                >{{ widgetRef.instanceProperties.label }}
                <span v-if="widgetRef.validationProperties.required || RulesService.isRequired(widget)">*</span></label
            >
            <div class="block">
                <UploadField
                    accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt"
                    v-model:uploadedFile="widgetRef.runtimeProperties.data"
                    class="widget-file-uploader"
                    :class="{ 'pointer-events-none': mode == 'edit' }"
                    :disabled="disabled"
                >
                    <div v-if="isFile || isStoredFile || isString" class="widget-file-preview">
                        <div class="flex justify-center items-center p-4 border border-solid border-neutral-normal-light">
                            <img :src="`/assets/icons/${fileIcon}`" class="icon-large" />
                        </div>
                        <div class="widget-actions">
                            <img src="/assets/icons/download.svg" class="icon-normal" @click="downloadFile($event)" />
                            <img src="/assets/icons/delete.svg" class="icon-normal" @click="removeFile($event)" />
                        </div>
                    </div>
                    <div v-else class="widget-file-browse">
                        <div class="widget-file-placeholder">
                            <div class="flex justify-center items-center p-4 border border-solid border-neutral-normal-light">
                                <img src="/assets/icons/file-upload.svg" class="icon-large" />
                            </div>
                            <div class="flex flex-col items-center">
                                <p><span class="text-green-normal underline">Selezione il file</span> da caricare.</p>
                                <p>(max 5mb?)</p>
                            </div>
                        </div>
                    </div>
                </UploadField>
            </div>
        </div>
        <slot></slot>
    </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useVModel } from '@vueuse/core';

import UploadField from '../../lib/UploadField.vue';
import RulesService from '../../../services/RulesService';

const props = defineProps({
    mode: {
        type: String,
        default: 'view',
    },
    widget: {
        type: Object,
        required: true,
    },
    disabled: {
        type: Boolean,
        default: false,
    },
});
const emit = defineEmits(['update:widget']);

const widgetRef = useVModel(props, 'widget', emit, { passive: true, deep: true });
const formWidgetRef = ref();

const isFile = computed(() => widgetRef.value.runtimeProperties.data instanceof File);
const isString = computed(() => typeof widgetRef.value.runtimeProperties.data === 'string');
const isStoredFile = computed(() => {
    const data = widgetRef.value.runtimeProperties.data;
    return data !== null && typeof data === 'object' && !(data instanceof File) && 'path' in data;
});

const fileExtension = computed(() => {
    if (isFile.value) {
        return widgetRef.value.runtimeProperties.data.name.split('.').pop().toLowerCase();
    }
    if (isStoredFile.value) {
        return widgetRef.value.runtimeProperties.data.path.split('?')[0].split('.').pop().toLowerCase();
    }
    if (isString.value) {
        return widgetRef.value.runtimeProperties.data.split('?')[0].split('.').pop().toLowerCase();
    }
    return '';
});

const ICON_MAP = {
    pdf: 'file-pdf.svg',
    doc: 'file-word.svg',
    docx: 'file-word.svg',
    xls: 'file-excel.svg',
    xlsx: 'file-excel.svg',
    ppt: 'file-powerpoint.svg',
    pptx: 'file-powerpoint.svg',
    txt: 'file-text.svg',
};

const fileIcon = computed(() => ICON_MAP[fileExtension.value] ?? 'file.svg');

const downloadFile = (event) => {
    event.stopPropagation();
    const data = widgetRef.value.runtimeProperties.data;
    const a = document.createElement('a');
    if (data instanceof File) {
        const url = URL.createObjectURL(data);
        a.href = url;
        a.download = data.name;
        a.click();
        URL.revokeObjectURL(url);
    } else if (isStoredFile.value) {
        a.href = data.path;
        a.download = data.name;
        a.click();
    } else {
        // Old format: plain string URL with no original filename stored
        a.href = data;
        const pathSegment = data.split('?')[0].split('/').pop();
        a.download = pathSegment || `file.${fileExtension.value}` || 'file';
        a.click();
    }
};

const removeFile = (event) => {
    widgetRef.value.runtimeProperties.data = null;
    event.stopPropagation();
};
</script>

<style lang="postcss" scoped>
.widget-file {
    @apply border-0 w-full;
}
.widget-file-uploader {
    @apply w-full justify-center;
    @apply border border-solid border-neutral-normal-light p-5;
    @apply relative;
    &[disabled='true'] {
        @apply bg-stone-100;
        pointer-events: none;
    }
}
.widget-file-browse {
    @apply p-1;
}
.widget-file-placeholder {
    @apply flex flex-col gap-4 items-center text-stone-400;

    img {
        @apply text-stone-400;
    }
}
.widget-file-item {
    @apply flex flex-col;
}
.widget-file-preview {
    @apply flex flex-col items-center gap-2;
}
.widget-file-name {
    @apply text-sm text-stone-600 text-center break-all;
}
.widget-actions {
    @apply absolute top-0 right-0 flex flex-row;
}
</style>
